import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MultiSelectDropDown from "../../../common/MultiSelectDropDown/MultiSelectDropDown";
import { TARGETING_FIELDS } from "../adManagementFormConfig";

const TargetingField = ({
  field,
  options,
  isViewMode,
  isLimited,
  showToAll,
  targeting,
  onChange,
}) => {
  const value = targeting[field.key];
  const disabled = isViewMode || isLimited || showToAll;
  const fieldId = `targeting-${field.key}`;

  if (field.type === "text" || field.type === "number") {
    const isNumeric = field.type === "number";
    return (
      <Grid item xs={12} sm={6} md={4} sx={{ padding: 2 }}>
        <label
          htmlFor={fieldId}
          style={{
            display: "block",
            marginBottom: 5,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {field.label}
        </label>
        <input
          id={fieldId}
          type={isNumeric ? "number" : "text"}
          className="form-label form-control"
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
          placeholder={`Enter ${field.label}`}
        />
      </Grid>
    );
  }

  if (field.type === "checkbox") {
    return (
      <Grid item xs={12} sm={6} md={4} sx={{ padding: 2 }}>
        <label
          style={{
            display: "block",
            marginBottom: 5,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {field.label}
        </label>
        <FormGroup row>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={Array.isArray(value) && value.includes(opt.value)}
                  onChange={(e) => {
                    const current = Array.isArray(value) ? value : [];
                    const updated = e.target.checked
                      ? [...current, opt.value]
                      : current.filter((v) => v !== opt.value);
                    onChange(field.key, updated);
                  }}
                  size="small"
                  disabled={disabled}
                />
              }
              label={<span style={{ fontSize: 13 }}>{opt.label}</span>}
            />
          ))}
        </FormGroup>
      </Grid>
    );
  }

  // Native single-select dropdown for type: "singleselect"
  if (field.type === "singleselect") {
    return (
      <Grid item xs={12} sm={6} md={4} sx={{ padding: 2 }}>
        <label
          htmlFor={fieldId}
          style={{
            display: "block",
            marginBottom: 5,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {field.label}
        </label>
        <select
          id={fieldId}
          name={field.key}
          value={value || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="form-control form-select"
          disabled={disabled}
        >
          <option value="">Select {field.label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Grid>
    );
  }

  // Default: multiselect dropdown — react-select via MultiSelectDropDown
  const selectedObjects = (Array.isArray(value) ? value : [])
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean);

  return (
    <Grid item xs={12} sm={6} md={4} sx={{ padding: 2 }}>
      <label
        htmlFor={fieldId}
        style={{
          display: "block",
          marginBottom: 5,
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        {field.label}
      </label>
      <MultiSelectDropDown
        id={fieldId}
        name={field.key}
        value={selectedObjects}
        options={options}
        onChange={(selected) =>
          onChange(
            field.key,
            (selected ?? []).map((o) => o.value),
          )
        }
        disabled={disabled}
        placeholder={`Select ${field.label}`}
      />
    </Grid>
  );
};

TargetingField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.string }),
  ).isRequired,
  isViewMode: PropTypes.bool.isRequired,
  isLimited: PropTypes.bool,
  showToAll: PropTypes.bool.isRequired,
  targeting: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
TargetingField.defaultProps = {
  isLimited: false,
};

const TargetingSection = ({
  accordionSx,
  showToAll,
  setShowToAll,
  isViewMode,
  isLimited,
  targeting,
  handleTargetingChange,
  targetingMultiplier,
  appliedFieldCount,
  targetingOptions,
}) => {
  /* When the user opts out of "Show to all", they must pick at least one
   * targeting field. Surface a non-blocking warning inside the section so
   * the requirement is obvious before they hit Save / Publish. */
  const showTargetingWarning =
    !isViewMode && !showToAll && appliedFieldCount === 0;
  return (
    <Accordion sx={accordionSx} className="app-accordion">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>Targeting</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2, backgroundColor: "#f8fcff" }}>
        {showTargetingWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select at least one targeting field, or enable &quot;Show to all
            Subscribers&quot;.
          </Alert>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={showToAll}
              onChange={(event) => setShowToAll(event.target.checked)}
              disabled={isViewMode || isLimited}
              size="small"
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Show to all Subscribers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All targeting fields are disabled and the ad is shown to all
                eligible subscribers.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: "flex-start" }}
        />
        <Grid container spacing={2}>
          {TARGETING_FIELDS.map((field) => (
            <TargetingField
              key={field.key}
              field={field}
              options={targetingOptions?.[field.key] ?? []}
              isViewMode={isViewMode}
              isLimited={isLimited}
              showToAll={showToAll}
              targeting={targeting}
              onChange={handleTargetingChange}
            />
          ))}
        </Grid>
        <Box
          sx={{
            mt: 2,
            p: "8px 12px",
            backgroundColor: "#e8f4fd",
            borderTop: "1px solid #b8ddf5",
          }}
        >
          <Typography variant="caption" sx={{ color: "#1976d2" }}>
            {appliedFieldCount} field
            {appliedFieldCount === 1 ? "" : "s"} applied
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

TargetingSection.propTypes = {
  accordionSx: PropTypes.object.isRequired,
  showToAll: PropTypes.bool.isRequired,
  setShowToAll: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool.isRequired,
  isLimited: PropTypes.bool,
  targeting: PropTypes.object.isRequired,
  handleTargetingChange: PropTypes.func.isRequired,
  targetingMultiplier: PropTypes.number.isRequired,
  appliedFieldCount: PropTypes.number.isRequired,
  targetingOptions: PropTypes.object,
};

TargetingSection.defaultProps = {
  isLimited: false,
  targetingOptions: {},
};

export default TargetingSection;
